import Vue from 'vue'
import { getEvent } from "../../api/events"

/**
 * events structure:
 * {
 *   [appName]: {
 *     [eventKey]: Event
 *   }
 * }
 */
const state = () => ({
	/** @type {Object.<string, Object.<string, Event>>} */
	events: {},
	loading: {},
	error: {}
})

// getters
const getters = {
	getEvent: (state) => (appName, eventKey) => {
		const appEvents = state.events[appName]
		return appEvents ? appEvents[eventKey] || null : null
	},

	/**
	 * @param {string} eventKey
	 * @returns {string} 'upcoming' | 'ongoing' | 'ended'
	 */
	getEventStatus: (state, getters, rootState, rootGetters) => (eventKey) => {
		const appName = rootGetters.getAppName

		let event = null
		if (
			state.events &&
			state.events[appName] &&
			state.events[appName][eventKey]
		) {
			event = state.events[appName][eventKey]
		}

		if (!event || !event.start_date || !event.end_date || !event.status) return null

		// check status
		if (event.status !== 'active') return 'ended'

		// check date. Only date not hours
		const now = new Date().setHours(0, 0, 0, 0)
		const start = new Date(event.start_date).setHours(0, 0, 0, 0)
		const end = new Date(event.end_date).setHours(0, 0, 0, 0)

		if (now < start) return 'upcoming'
		if (now > end) return 'ended'
		return 'ongoing'
	},

	getEventStartDate: (state, getters, rootState, rootGetters) => (eventKey) => {
		const event = getters.getEvent(rootGetters['getAppName'], eventKey)
		if (!event) return null
		return event.start_date || null
	},

	getEventEndDate: (state, getters, rootState, rootGetters) => (eventKey) => {
		const event = getters.getEvent(rootGetters['getAppName'], eventKey)
		if (!event) return null
		return event.end_date || null
	},

	isLoading: (state, getters, rootState, rootGetters) => (eventKey) => {
		const appName = rootGetters['getAppName']
		return !!state.loading[`${appName}_${eventKey}`]
	},

	getError: (state, getters, rootState, rootGetters) => (eventKey) => {
		const appName = rootGetters['getAppName']
		return state.error[`${appName}_${eventKey}`] || null
	}
}

// actions
const actions = {
	async fetchEvent({ commit, state, rootGetters }, eventKey) {


		const appName = rootGetters['getAppName']
		const paymentMethod = rootGetters['paymentMethod']
		const cacheKey = `${appName}_${eventKey}`

		if (state.events[appName]) {
			// prevent duplicate requests
			if (state.events[appName][eventKey] || state.loading[cacheKey]) return
		}

		commit('SET_LOADING', { key: cacheKey, value: true })
		commit('SET_ERROR', { key: cacheKey, value: null })

		try {
			const response = await getEvent({
				campaign_key: eventKey,
				app_name: appName,
				payment_method: paymentMethod
			})
			commit('SET_EVENT', { appName, eventKey, data: response.data[0] })
		} catch (err) {
			commit('SET_ERROR', { key: cacheKey, value: err.message })
		} finally {
			commit('SET_LOADING', { key: cacheKey, value: false })
		}
	}
}

// mutations
const mutations = {
	SET_LOADING(state, { key, value }) {
		Vue.set(state.loading, key, value)
	},

	SET_ERROR(state, { key, value }) {
		Vue.set(state.error, key, value)
	},

	SET_EVENT(state, { appName, eventKey, data }) {
		// Create appName object if it doesn't exist
		if (!state.events[appName]) {
			Vue.set(state.events, appName, {})
		}

		Vue.set(state.events[appName], eventKey, data)
	}
}

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
}